'use client';

import { useId, useState, useTransition } from 'react';
import {
    Alert,
    Box,
    ButtonBase,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CloseIcon from '@mui/icons-material/Close';
import { Enum_Product_Category, Product } from '@/app/lib/generated/graphql-types';
import {
    getShopProducts,
    ShopProductPage,
} from '@/app/features/sites/shop/data-access/queries';
import MediaView from '@/app/features/shared/media-view/ui/public/MediaView.public';
import PaginationControl from '@/app/features/shared/__to_be_integrated/ui/pagination/ui/PaginationControl';
import { Markdown } from '@/app/features/shared/markdown/Markdown';
import VisiblyScrollableV2 from '@/app/features/shared/__to_be_integrated/ui/v2/VisiblyScrollable';
import { signatureBorderStyle } from '@/app/theme/theme-tokens';

type ProductSectionKey = 'publications' | 'other';

export default function ShopProducts({
    highlights,
    publications: initialPublications,
    other: initialOther,
    companion,
}: {
    highlights: ShopProductPage;
    publications: ShopProductPage;
    other: ShopProductPage;
    companion?: React.ReactNode;
}) {
    const [publications, setPublications] = useState(initialPublications);
    const [other, setOther] = useState(initialOther);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [paginationMessage, setPaginationMessage] = useState('');
    const [paginationError, setPaginationError] = useState('');
    const [isPending, startTransition] = useTransition();

    function changePage(section: ProductSectionKey, page: number) {
        const current = section === 'publications' ? publications : other;
        if (page === current.pagination.page || isPending) return;

        const categories = section === 'publications'
            ? [Enum_Product_Category.Companion, Enum_Product_Category.Publications]
            : [Enum_Product_Category.Other];
        const sectionLabel = section === 'publications'
            ? 'Museumseigene Publikationen'
            : 'Weitere Publikationen';

        setPaginationError('');
        setPaginationMessage(`${sectionLabel}, Seite ${page} wird geladen.`);

        startTransition(async () => {
            try {
                const result = await getShopProducts({
                    categories,
                    pagination: { page, pageSize: current.pagination.pageSize },
                    ...(section === 'publications'
                        ? { sort: ['category:asc', 'rank:asc', 'title:asc'] }
                        : {}),
                });
                if (result.error || !result.data) {
                    setPaginationError(
                        `${sectionLabel} konnten nicht geladen werden. Bitte versuchen Sie es erneut.`
                    );
                    setPaginationMessage('');
                    return;
                }

                if (section === 'publications') setPublications(result.data);
                else setOther(result.data);
                setPaginationMessage(
                    `${sectionLabel}, Seite ${result.data.pagination.page} von ${result.data.pagination.pageCount} wurde geladen.`
                );
            } catch {
                setPaginationError(
                    `${sectionLabel} konnten nicht geladen werden. Bitte versuchen Sie es erneut.`
                );
                setPaginationMessage('');
            }
        });
    }

    return (
        <Stack spacing={{ xs: 6, md: 9 }} aria-busy={isPending}>
            <Box
                role="status"
                aria-live="polite"
                aria-atomic="true"
                sx={visuallyHiddenStyles}
            >
                {paginationMessage}
            </Box>
            {paginationError && <Alert severity="error">{paginationError}</Alert>}

            {highlights.items.length > 0 && (
                <ProductSection title="Highlights">
                    <Stack component="ul" spacing={4} sx={productListStyles}>
                        {highlights.items.map((product) => (
                            <Box component="li" key={product.documentId}>
                                <HighlightProduct
                                    product={product}
                                    onOpen={() => setSelectedProduct(product)}
                                />
                            </Box>
                        ))}
                    </Stack>
                </ProductSection>
            )}

            <ProductSection title="Museumseigene Publikationen" description={companion}>
                <Grid
                    component="ul"
                    container
                    columns={{ xs: 2, sm: 4, md: 5 }}
                    spacing={{ xs: 2, md: 4 }}
                    sx={productListStyles}
                >
                    {publications.items.map((product) => (
                        <Grid component="li" key={product.documentId} size={1}>
                            <ProductTile product={product} onOpen={() => setSelectedProduct(product)} />
                        </Grid>
                    ))}
                </Grid>
                <ShopPagination
                    label="Museumseigene Publikationen"
                    page={publications}
                    disabled={isPending}
                    onPageChange={(page) => changePage('publications', page)}
                />
            </ProductSection>

            {other.items.length > 0 && (
                <ProductSection title="Weitere Publikationen">
                    <Stack component="ul" divider={<Divider flexItem />} sx={{ listStyle: 'none', p: 0, m: 0 }}>
                        {other.items.map((product) => (
                            <Box component="li" key={product.documentId}>
                                <ButtonBase
                                    onClick={() => setSelectedProduct(product)}
                                    sx={{ width: '100%', p: 2, textAlign: 'left', borderRadius: 1 }}
                                    aria-label={`Details zu ${product.title} öffnen`}
                                    aria-haspopup="dialog"
                                >
                                    <Grid container spacing={2} sx={{ width: '100%', alignItems: 'center' }}>
                                        <Grid size={{ xs: 12, md: 4 }}>
                                            <Typography variant="h3" component="h3">
                                                {product.title}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 8, md: 6 }}>
                                            <Typography variant="body1">{product.subtitle}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 4, md: 2 }}>
                                            <Typography variant="h6" component="p" textAlign="right">
                                                {formatPrice(product.price)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </ButtonBase>
                            </Box>
                        ))}
                    </Stack>
                    <ShopPagination
                        label="Weitere Publikationen"
                        page={other}
                        disabled={isPending}
                        onPageChange={(page) => changePage('other', page)}
                    />
                </ProductSection>
            )}

            <ProductDialog product={selectedProduct} onClose={() => setSelectedProduct(null)} />
        </Stack>
    );
}

function ProductSection({
    title,
    description,
    children,
}: {
    title: string;
    description?: React.ReactNode;
    children: React.ReactNode;
}) {
    const titleId = useId();

    return (
        <Stack component="section" spacing={3} aria-labelledby={titleId}>
            <Divider>
                <Typography id={titleId} variant="h2" component="h2" color="text.secondary">
                    {title}
                </Typography>
            </Divider>
            {description && (
                <Typography variant="subtitle1" textAlign="center">
                    {description}
                </Typography>
            )}
            {children}
        </Stack>
    );
}

function HighlightProduct({ product, onOpen }: { product: Product; onOpen: () => void }) {
    return (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 4 } }}>
            <Grid container spacing={4} alignItems="center">
                <Grid size={{ xs: 12, md: 4 }}>
                    <ProductImage product={product} aspectRatio="1 / 1" />
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                    <Stack spacing={2} alignItems="flex-start">
                        <ButtonBase
                            onClick={onOpen}
                            aria-label={`Details zu ${product.title} öffnen`}
                            aria-haspopup="dialog"
                            sx={{ textAlign: 'left', borderRadius: 1 }}
                        >
                            <Typography variant="h2" component="h3">
                                {product.title}
                            </Typography>
                        </ButtonBase>
                        {product.subtitle && <Typography variant="subtitle1">{product.subtitle}</Typography>}
                        {product.description && (
                            <VisiblyScrollableV2
                                scrollContainerAriaLabel={`Beschreibung von ${product.title}`}
                                sx={{
                                    width: '100%',
                                    minHeight: 0,
                                    maxHeight: { md: 250 },
                                }}
                            >
                                <Markdown value={product.description} />
                            </VisiblyScrollableV2>
                        )}
                        <Typography variant="h5" component="p" fontWeight="bold">
                            {formatPrice(product.price)}
                        </Typography>
                    </Stack>
                </Grid>
            </Grid>
        </Paper>
    );
}

function ProductTile({ product, onOpen }: { product: Product; onOpen: () => void }) {
    return (
        <ButtonBase
            onClick={onOpen}
            aria-label={`Details zu ${product.title} öffnen`}
            aria-haspopup="dialog"
            sx={{ display: 'block', width: '100%', height: '100%', textAlign: 'left', borderRadius: 1 }}
        >
            <Stack spacing={2} sx={{ height: '100%' }}>
                <ProductImage
                    product={product}
                    aspectRatio="25 / 35"
                    objectFit="cover"
                />
                <Typography variant="body1" component="h3" fontWeight="bold">
                    {product.title}
                </Typography>
                {product.subtitle && <Typography variant="body2">{product.subtitle}</Typography>}
                <Typography variant="body1" fontWeight="bold" sx={{ pt: 1 }}>
                    {formatPrice(product.price)}
                </Typography>
            </Stack>
        </ButtonBase>
    );
}

function ProductImage({
    product,
    aspectRatio,
    objectFit = 'contain',
}: {
    product: Product;
    aspectRatio: string;
    objectFit?: 'contain' | 'cover';
}) {
    const image = product.image ?? '/assets/placeholder/placeholder.webp';

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                width: '100%',
                aspectRatio,
                overflow: 'hidden',
                ...signatureBorderStyle,
            }}
        >
            <MediaView
                src={image}
                alt={product.image?.alternativeText ?? product.title}
                fill
                showLoadingSpinner
                objectFit={objectFit}
                slots={{
                    image: {
                        sizes: '(max-width: 600px) 50vw, (max-width: 1200px) 25vw, 320px',
                        quality: 75,
                    },
                }}
            />
        </Box>
    );
}

function ShopPagination({
    label,
    page,
    disabled,
    onPageChange,
}: {
    label: string;
    page: ShopProductPage;
    disabled: boolean;
    onPageChange: (page: number) => void;
}) {
    if ((page.pagination.pageCount ?? 1) <= 1) return null;

    return (
        <PaginationControl
            ariaLabel={`Seitennavigation für ${label}`}
            disabled={disabled}
            usePaginationDelegate={() => ({
                pageInfo: page.pagination,
                setPage: onPageChange,
                setPageSize: () => undefined,
                pageSizes: [page.pagination.pageSize],
                showPageSize: false,
            })}
        />
    );
}

function ProductDialog({ product, onClose }: { product: Product | null; onClose: () => void }) {
    const titleId = useId();
    const contentLength = [product?.subtitle, product?.description]
        .filter(Boolean)
        .join(' ')
        .length;
    const dialogViewportScale = contentLength > 1200
        ? 88
        : contentLength > 500
            ? 74
            : 58;
    const dialogWidth = `${dialogViewportScale}vw`;
    const dialogHeight = `${dialogViewportScale}dvh`;

    return (
        <Dialog
            open={Boolean(product)}
            onClose={onClose}
            aria-labelledby={titleId}
            maxWidth={false}
            scroll="paper"
            sx={{
                '& .MuiDialog-paper': {
                    width: dialogWidth,
                    minWidth: dialogWidth,
                    maxWidth: dialogWidth,
                    height: dialogHeight,
                    minHeight: dialogHeight,
                    maxHeight: dialogHeight,
                    m: 0,
                },
            }}
        >
            {product && (
                <>
                    <DialogTitle id={titleId} component="h2" sx={{ pr: 8 }}>
                        {product.title}
                        <IconButton
                            autoFocus
                            onClick={onClose}
                            aria-label="Produktdetails schließen"
                            sx={{ position: 'absolute', right: 8, top: 8, width: 48, height: 48 }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent
                        dividers
                        sx={{
                            display: 'flex',
                            flex: '1 1 auto',
                            minHeight: 0,
                            width: '100%',
                        }}
                    >
                        <Grid
                            container
                            spacing={4}
                            alignItems="center"
                            sx={{ flex: 1, width: '100%', minHeight: 0 }}
                        >
                            {product.image && (
                                <Grid
                                    size={{ xs: 12, md: 4 }}
                                    sx={{
                                        display: 'flex',
                                        height: { xs: 'auto', md: '100%' },
                                        minHeight: 0,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            flex: 1,
                                            width: '100%',
                                            height: { xs: 'auto', md: '100%' },
                                            minHeight: 0,
                                            aspectRatio: { xs: '1 / 1', md: 'auto' },
                                        }}
                                    >
                                        <MediaView
                                            src={product.image}
                                            alt={product.image.alternativeText ?? product.title}
                                            fill
                                            objectFit="contain"
                                            modalViewType="click"
                                            slots={{ image: { sizes: '(max-width: 900px) 100vw, 40vw' } }}
                                        />
                                    </Box>
                                </Grid>
                            )}
                            <Grid
                                size={{ xs: 12, md: product.image ? 8 : 12 }}
                                sx={{
                                    display: 'flex',
                                    height: { xs: 'auto', md: '100%' },
                                    minHeight: 0,
                                }}
                            >
                                <Stack
                                    spacing={2}
                                    sx={{
                                        flex: 1,
                                        width: '100%',
                                        height: { xs: 'auto', md: '100%' },
                                        minHeight: 0,
                                        justifyContent: 'center',
                                    }}
                                >
                                    {product.subtitle && (
                                        <Typography variant="h5" component="p">{product.subtitle}</Typography>
                                    )}
                                    {product.description && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flex: '0 1 auto',
                                                height: { xs: 'min(24vh, 240px)', md: 'auto' },
                                                maxHeight: { xs: 'min(24vh, 240px)', md: '100%' },
                                                minHeight: 0,
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <VisiblyScrollableV2 scrollContainerAriaLabel="Produktbeschreibung">
                                                <Markdown value={product.description} />
                                            </VisiblyScrollableV2>
                                        </Box>
                                    )}
                                    <Typography variant="h4" component="p" fontWeight="bold">
                                        {formatPrice(product.price)}
                                    </Typography>
                                </Stack>
                            </Grid>
                        </Grid>
                    </DialogContent>
                </>
            )}
        </Dialog>
    );
}

const productListStyles = {
    listStyle: 'none',
    p: 0,
    m: 0,
} as const;

const visuallyHiddenStyles = {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    p: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1,
} as const;

function formatPrice(price: number) {
    if (price === 0) return 'Kostenfrei';
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
    }).format(price);
}
